import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { paymentService } from '../services/paymentService';
import toast from 'react-hot-toast';
import { useSEO } from '../hooks/useSEO';

const ConfirmationPage = () => {
  useSEO({
    title: "Order Confirmation | AM CARDMATE",
    description: "Thank you for your order! Your payment has been processed and your digital business cards are being prepared.",
    keywords: "order confirmation, payment success, thank you, order complete"
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orderCreated, setOrderCreated] = useState(false);
  const [creating, setCreating] = useState(true);
  const [error, setError] = useState(null);
  const orderCreationAttempted = useRef(false);

  const { paymentIntent, orderData, cart } = location.state || {};

  useEffect(() => {
    if (orderCreated && paymentIntent) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "Order",
        "orderNumber": paymentIntent.id,
        "orderStatus": "OrderPaymentDue",
        "orderDate": new Date().toISOString(),
        "acceptedOffer": cart?.items?.map(item => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": item.name
          },
          "price": item.price,
          "priceCurrency": "GBP"
        })),
        "customer": {
          "@type": "Person",
          "email": user?.email
        }
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [orderCreated, paymentIntent, cart, user]);

  useEffect(() => {
    if (!paymentIntent || !orderData || !cart) {
      navigate('/cart');
      return;
    }

    if (!orderCreationAttempted.current) {
      orderCreationAttempted.current = true;
      createOrder();
    }
  }, [paymentIntent, orderData, cart, navigate]);

  const createOrder = async () => {
    if (!user || !paymentIntent) return;

    try {
      setCreating(true);
      setError(null);
      
      const orderDetails = {
        paymentIntentId: paymentIntent.id,
        items: cart.items,
        total: cart.total,
        shippingCost: orderData.shippingCost || 0,
        paymentAmount: paymentIntent.amount ? paymentIntent.amount / 100 : cart.total + (orderData.shippingCost || 0),
        shippingInfo: orderData.shippingInfo,
        customerInfo: {
          email: user.email,
          name: user.displayName || `${orderData.shippingInfo.firstName} ${orderData.shippingInfo.lastName}`
        },
        paymentStatus: paymentIntent.status === 'succeeded' ? 'completed' : 'pending',
        paymentMethod: 'card',
        currency: paymentIntent.currency || 'gbp'
      };

      const result = await paymentService.createOrder(user.uid, orderDetails);
      
      if (result.data && !result.error) {
        setOrderCreated(true);
        toast.success('Order created successfully!');
        
        try {
          await paymentService.sendConfirmationEmail(user.email, {
            ...result.data,
            orderNumber: result.data.orderNumber,
            customerInfo: orderDetails.customerInfo,
            items: cart.items,
            total: cart.total,
            shippingInfo: orderData.shippingInfo,
            shippingCost: orderData.shippingCost || 0
          });
        } catch (emailError) {
          console.error('Email sending failed:', emailError);
        }
      } else {
        throw new Error(result.error || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setError(error.message);
      toast.error('Failed to create order. Please contact support.');
    } finally {
      setCreating(false);
    }
  };

  if (creating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        
        <div className="text-center relative z-10">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <FontAwesomeIcon icon="spinner" className="text-white text-3xl animate-spin" />
          </div>
          <h2 className="text-h4 text-dark mb-4">Creating Your Order</h2>
          <p className="text-body text-gray-600">Please wait while we process your order...</p>
        </div>
      </div>
    );
  }

  if (error || !orderCreated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        
        <div className="max-w-md w-full text-center relative z-10">
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
            <div className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <FontAwesomeIcon icon="exclamation-triangle" className="text-white text-3xl" />
            </div>
            <h2 className="text-h4 text-dark mb-4">Order Creation Failed</h2>
            <p className="text-body text-gray-600 mb-6">
              {error || 'There was an issue creating your order. Please contact support.'}
            </p>
            <div className="space-y-3">
              <Link to="/account?tab=orders" className="btn btn-primary w-full">
                <FontAwesomeIcon icon="user" />
                <span>Go to Account</span>
              </Link>
              <Link to="/contact" className="btn btn-outline w-full">
                <FontAwesomeIcon icon="envelope" />
                <span>Contact Support</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const shippingCost = orderData?.shippingCost || 0;
  const subtotal = cart?.total || 0;
  const totalPaid = paymentIntent?.amount ? paymentIntent.amount / 100 : subtotal + shippingCost;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 sm:py-12 px-4 sm:px-6">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
      
      <div className="max-w-4xl mx-auto relative z-10 pt-20 md:pt-24 lg:pt-32">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-pulse">
            <FontAwesomeIcon icon="check" className="text-white text-4xl" />
          </div>
          <h1 className="text-h2 text-dark mb-4">
            Payment <span className="text-gradient-blue-pink">Successful!</span>
          </h1>
          <p className="text-body-lg text-gray-600">
            Thank you for your order. We've received your payment and will process your order shortly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Details Card */}
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6">
            <h2 className="text-h5 text-dark mb-6 flex items-center gap-2">
              <FontAwesomeIcon icon="receipt" className="text-blue-500" />
              Order Details
            </h2>
            
            {/* Payment Confirmed Badge */}
            <div className="mb-6 p-4 bg-green-50 rounded-2xl border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon="credit-card" className="text-green-600" />
                <span className="text-body font-semibold text-green-800">Payment Confirmed</span>
              </div>
              <p className="text-body-sm text-green-700">
                Payment ID: {paymentIntent?.id}
              </p>
              <p className="text-body-sm text-green-700">
                Amount: £{totalPaid.toFixed(2)}
              </p>
              <p className="text-body-sm text-green-700">
                Status: {paymentIntent?.status || 'Completed'}
              </p>
            </div>

            {/* Items Ordered */}
            <div className="space-y-4 mb-6">
              <h3 className="text-body font-semibold text-dark">Items Ordered:</h3>
              {cart?.items?.map((item, index) => (
                <div key={`${item.id}-${index}`} className="flex items-center gap-4 p-3 bg-gray-50 rounded-2xl">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-contain rounded-xl" />
                    ) : (
                      <FontAwesomeIcon icon="credit-card" className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-body font-medium text-dark">{item.name}</h4>
                    <p className="text-body-sm text-gray-600">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-body font-bold">£{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Price Summary */}
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between text-body-sm">
                <span>Subtotal:</span>
                <span>£{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-body-sm">
                <span>Shipping:</span>
                <span className={shippingCost === 0 ? "text-green-600 font-medium" : ""}>
                  {shippingCost === 0 ? 'FREE ✨' : `£${shippingCost.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between text-h5 border-t border-gray-200 pt-2">
                <span>Total Paid:</span>
                <span className="text-gradient-blue-pink">
                  £{totalPaid.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Shipping Information Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6">
              <h2 className="text-h5 text-dark mb-6 flex items-center gap-2">
                <FontAwesomeIcon icon="truck" className="text-blue-500" />
                Shipping Information
              </h2>
              
              <div className="space-y-3">
                <div>
                  <p className="text-body font-medium">
                    {orderData?.shippingInfo?.firstName} {orderData?.shippingInfo?.lastName}
                  </p>
                  <p className="text-body-sm text-gray-600">{orderData?.shippingInfo?.email}</p>
                  {orderData?.shippingInfo?.phone && (
                    <p className="text-body-sm text-gray-600">{orderData?.shippingInfo?.phone}</p>
                  )}
                </div>
                
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-body text-gray-800">{orderData?.shippingInfo?.address}</p>
                  <p className="text-body text-gray-800">
                    {orderData?.shippingInfo?.city}, {orderData?.shippingInfo?.postalCode}
                  </p>
                  <p className="text-body text-gray-800">{orderData?.shippingInfo?.country}</p>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-body font-medium text-gray-700">Shipping Cost:</span>
                    <span className={`text-body font-bold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                      {shippingCost === 0 ? 'FREE ✨' : `£${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                  {shippingCost === 0 && subtotal >= 50 && (
                    <p className="text-body-sm text-green-600 mt-1">
                      🎉 Free shipping applied for orders over £50!
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* What Happens Next Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6">
              <h2 className="text-h5 text-dark mb-6 flex items-center gap-2">
                <FontAwesomeIcon icon="list-check" className="text-purple-500" />
                What Happens Next?
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-body-sm font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-body font-semibold text-dark">Order Confirmation</h3>
                    <p className="text-body-sm text-gray-600">You'll receive an email confirmation shortly with your order details.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-body-sm font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-body font-semibold text-dark">Processing</h3>
                    <p className="text-body-sm text-gray-600">We'll prepare your NFC cards with your custom details (1-2 business days).</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-body-sm font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-body font-semibold text-dark">Shipping</h3>
                    <p className="text-body-sm text-gray-600">
                      Your order will be shipped within 2-3 business days via tracked delivery
                      {shippingCost === 0 ? ' (FREE shipping!)' : ''}.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Link to="/account?tab=orders" className="w-full btn btn-primary">
                <FontAwesomeIcon icon="user" />
                <span>View Order Status</span>
              </Link>
              
              <Link to="/nfc-cards" className="w-full btn btn-outline">
                <FontAwesomeIcon icon="shopping-bag" />
                <span>Continue Shopping</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-12 bg-white/50 backdrop-blur-sm rounded-2xl p-6 text-center">
          <h3 className="text-h5 text-dark mb-2">Need Help?</h3>
          <p className="text-body text-gray-600 mb-4">
            If you have any questions about your order, our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="btn btn-outline">
              <FontAwesomeIcon icon="envelope" />
              <span>Contact Support</span>
            </Link>
            <a href="tel:+441234567890" className="btn btn-outline">
              <FontAwesomeIcon icon="phone" />
              <span>Call Us</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;